import { useEffect, useMemo, useRef } from "react";
import { Controller, type Control, type FieldErrors, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { AppLanguageType } from "@noa/i18n";
import { Alert, Badge, FormItem, Input, Select, Tabs, TagsInput, TextArea } from "~/components/ui";
import type { LexiconSubject } from "~/common/lexicon-subject";
import { compactFormItemStyle, requiredFieldsFor, subjectsToOptions } from "~/utils/lexicon";
import type { LexiconConceptForm } from "~/utils/lexicon";

type LexiconConceptFieldsProps = {
  control: Control<LexiconConceptForm>;
  errors: FieldErrors<LexiconConceptForm>;
  language: AppLanguageType;
  subjects: LexiconSubject[];
  /** Re-mounts the tabs when the caller's identity changes (e.g. selected subject). */
  tabsKey?: string;
  /** Compact spacing for the in-chat action card, where vertical room is scarce. */
  size?: "default" | "small";
  /**
   * Controls the visible tab. Pass with `onTabChange` when the caller needs to
   * steer it — e.g. to reveal a hidden tab holding a validation error. Omit
   * both to leave the tabs uncontrolled, opening on `language`.
   */
  activeKey?: string;
  onTabChange?: (key: string) => void;
  /** Focuses the subject field on mount, where it is the first thing to fill. */
  autoFocusSubject?: boolean;
};

/**
 * The bilingual concept form body — subject, term, description, acronyms, synonyms
 * across FR/EN tabs.
 *
 * Shared by all three surfaces that edit a concept — `CreateConceptModal` and
 * `UpdateConceptModal` on the lexicon screen, and the `add-lexicon-term` action
 * card in the chat — so they cannot drift: a field added here appears in every
 * one, in both languages. The parent owns the `useForm` instance and the submit
 * path; this component only renders fields against the control it is handed.
 */
export function LexiconConceptFields({
  control,
  errors,
  language,
  subjects,
  tabsKey,
  size = "default",
  activeKey,
  onTabChange,
  autoFocusSubject = false,
}: LexiconConceptFieldsProps) {
  const { t } = useTranslation("lexicon");

  const [termFr, termEn, descFr, descEn, acronymsFr, acronymsEn, synonymsFr, synonymsEn] = useWatch({
    control,
    name: [
      "term_fr",
      "term_en",
      "description_fr",
      "description_en",
      "acronyms_fr",
      "acronyms_en",
      "synonyms_fr",
      "synonyms_en",
    ],
  });

  const warningState = useMemo(() => {
    const frenchEmpty = !termFr && !descFr && !acronymsFr?.length && !synonymsFr?.length;
    const englishEmpty = !termEn && !descEn && !acronymsEn?.length && !synonymsEn?.length;

    return {
      showFrenchWarning: !frenchEmpty && englishEmpty,
      showEnglishWarning: !englishEmpty && frenchEmpty,
    };
  }, [termFr, termEn, descFr, descEn, acronymsFr, acronymsEn, synonymsFr, synonymsEn]);

  const { frenchSubjectOptions, englishSubjectOptions } = useMemo(
    () => ({
      frenchSubjectOptions: subjectsToOptions(subjects, "fr"),
      englishSubjectOptions: subjectsToOptions(subjects, "en"),
    }),
    [subjects]
  );

  const descriptionRows = size === "small" ? 2 : 3;

  // The tab on screen at mount — `activeKey` when controlled, else the
  // `defaultActiveKey` the Tabs below fall back to. Both tabs render, so focus
  // has to follow the visible one or it lands on a hidden field.
  //
  // Captured in a ref, not recomputed: the subject select is focused once at mount,
  // so following the live `activeKey` would only re-point the ref on every switch.
  const initialTab = useRef(activeKey ?? language).current;
  const subjectTriggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (autoFocusSubject) subjectTriggerRef.current?.focus();
  }, [autoFocusSubject]);

  const renderTab = (locale: "fr" | "en") => {
    const isFrench = locale === "fr";
    const showWarning = isFrench ? warningState.showFrenchWarning : warningState.showEnglishWarning;
    const otherLanguageLabel = isFrench ? t("tabs.english") : t("tabs.french");
    const termField = isFrench ? "term_fr" : "term_en";
    const descriptionField = isFrench ? "description_fr" : "description_en";
    const acronymsField = isFrench ? "acronyms_fr" : "acronyms_en";
    const synonymsField = isFrench ? "synonyms_fr" : "synonyms_en";
    const subjectOptions = isFrench ? frenchSubjectOptions : englishSubjectOptions;

    return (
      <>
        {showWarning && (
          <Alert
            message={t("createConceptModal.incompleteSection", { language: otherLanguageLabel })}
            type="warning"
            showIcon
            className="mb-3"
          />
        )}
        <FormItem
          required
          label={t("fields.subject")}
          validateStatus={errors.subject_id ? "error" : ""}
          help={errors.subject_id ? t("common:formValidations.required") : undefined}
          className={compactFormItemStyle}
        >
          <Controller
            name="subject_id"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                ref={(element) => {
                  field.ref(element);
                  if (locale === initialTab) subjectTriggerRef.current = element;
                }}
                options={subjectOptions}
                placeholder={t("fields.subjectPlaceholder")}
              />
            )}
          />
        </FormItem>
        <FormItem
          required
          label={t("fields.term")}
          validateStatus={errors[termField] ? "error" : ""}
          help={errors[termField] ? t("common:formValidations.required") : undefined}
          className={compactFormItemStyle}
        >
          <Controller name={termField} control={control} render={({ field }) => <Input {...field} />} />
        </FormItem>
        <FormItem
          required
          label={t("fields.description")}
          validateStatus={errors[descriptionField] ? "error" : ""}
          help={errors[descriptionField] ? t("common:formValidations.required") : undefined}
          className={compactFormItemStyle}
        >
          <Controller
            name={descriptionField}
            control={control}
            render={({ field }) => <TextArea rows={descriptionRows} {...field} />}
          />
        </FormItem>
        <FormItem label={t("fields.acronyms")} className={compactFormItemStyle}>
          <Controller
            name={acronymsField}
            control={control}
            render={({ field }) => <TagsInput {...field} tokenSeparators={[","]} />}
          />
        </FormItem>
        <FormItem label={t("fields.synonyms")} className={compactFormItemStyle}>
          <Controller
            name={synonymsField}
            control={control}
            render={({ field }) => <TagsInput {...field} tokenSeparators={[","]} />}
          />
        </FormItem>
      </>
    );
  };

  // A tab's own errors are invisible while it is hidden, so mark the label too —
  // the badge carries the state, not colour alone.
  const tabLabel = (locale: "fr" | "en", label: string) =>
    requiredFieldsFor(locale).some((f) => errors[f]) ? (
      <span>
        {label} <Badge status="error" title={t("createConceptModal.tabHasErrors")} />
      </span>
    ) : (
      label
    );

  // Controlled only when the caller supplies both, so existing callers keep the
  // uncontrolled behaviour they rely on.
  const controlled = activeKey !== undefined && onTabChange !== undefined;

  return (
    <Tabs
      key={tabsKey}
      {...(controlled ? { activeKey, onChange: onTabChange } : { defaultActiveKey: language })}
      size={size === "small" ? "small" : "middle"}
      items={[
        { key: "fr", label: tabLabel("fr", t("tabs.french")), children: renderTab("fr") },
        { key: "en", label: tabLabel("en", t("tabs.english")), children: renderTab("en") },
      ]}
    />
  );
}
